import { Dispatch, SetStateAction, useEffect, useReducer, useState } from 'react'
import { SelectChangeEvent } from '@mui/material'
import { useCmsApi } from '@/services/cms-api'
import { useMobiApi } from '@/services/mobi-api'
import {
  DistrictFilter,
  QuarterFilter,
  TravelPointFilter
} from '@/services/cms-api/network-coverage/types'
import { NETWORK_COVERAGE_URL } from '@/utils/constants'
import {
  Building,
  City,
  District,
  MapState,
  Quarter,
  SelectItem,
  TravelPoint
} from './types'
import { INIT_CENTER, TAB_ID } from './networkCoverageConstants'
import { localeName, sortByNameMn, sortCities, toOptional } from './helpers'

const initialMapState: MapState = {
  kmlContent: '',
  kmlHbb: `${NETWORK_COVERAGE_URL}/Urhiin_Internet.kml`,
  kmlWifi: '',
  center: INIT_CENTER,
  zoom: 12
}

interface NetworkCoverageStore {
  mapState: MapState
  setMapState: Dispatch<Partial<MapState>>
  activeTab: number
  setActiveTab: Dispatch<SetStateAction<number>>
  cities: City[]
  selectedCity: City | null
  setSelectedCity: Dispatch<SetStateAction<City | null>>
  districts: District[]
  selectedDistrict: District | null
  quarters: Quarter[]
  selectedQuarter: Quarter | null
  buildings: Building[]
  selectedBuilding: Building | null
  travelPoints: TravelPoint[]
  selectedTravelPoint: TravelPoint | null
  isHbbModalOpen: boolean
  setIsHbbModalOpen: Dispatch<SetStateAction<boolean>>
  isNetworkModalOpen: boolean
  setIsNetworkModalOpen: Dispatch<SetStateAction<boolean>>
  userLocation: { lat: number, lng: number } | null
  setUserLocation: Dispatch<SetStateAction<{ lat: number, lng: number } | null>>
  handleCitySelect: (zipcode: string, options?: { updateMapCenter?: boolean, onCityFound?: (city: City) => void }) => void
  handleDistrictSelect: (zipcode: string, options?: { updateMapCenter?: boolean }) => void
  handleQuarterSelect: (event: SelectChangeEvent<string>) => void
  handleBuildingSelect: (event: SelectChangeEvent<string>) => void
  handleNetworkTypeSelect: (event: SelectChangeEvent<string>) => void
  handleTravelPointSelect: (item: TravelPoint) => void
  fetchTravelPoint: (query: TravelPointFilter) => Promise<void>
  cityItems: SelectItem[]
  districtItems: SelectItem[]
  quarterItems: SelectItem[]
  buildingItems: SelectItem[]
  ncCityItems: SelectItem[]
  ncDistrictItems: SelectItem[]
}

export function useNetworkCoverage (locale: string): NetworkCoverageStore {
  const { networkCoverage: NetworkCoverageAPI } = useMobiApi()
  const { getCityList, getDistrictList, getQuarterList, getTravelPoint } = useCmsApi()

  const [mapState, setMapState] = useReducer(
    (state: MapState, newState: Partial<MapState>) => ({ ...state, ...newState }),
    initialMapState
  )

  const [activeTab, setActiveTab] = useState(TAB_ID.HBB)
  const [cities, setCities] = useState<City[]>([])
  const [selectedCity, setSelectedCity] = useState<City | null>(null)
  const [districts, setDistricts] = useState<District[]>([])
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(null)
  const [quarters, setQuarters] = useState<Quarter[]>([])
  const [selectedQuarter, setSelectedQuarter] = useState<Quarter | null>(null)
  const [buildings, setBuildings] = useState<Building[]>([])
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null)
  const [travelPoints, setTravelPoints] = useState<TravelPoint[]>([])
  const [selectedTravelPoint, setSelectedTravelPoint] = useState<TravelPoint | null>(null)
  const [isHbbModalOpen, setIsHbbModalOpen] = useState(false)
  const [isNetworkModalOpen, setIsNetworkModalOpen] = useState(false)
  const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null)

  // Fetch cities once on mount
  useEffect(() => { void fetchCities() }, [])

  // Reset selections and update map state on tab change
  useEffect(() => {
    setSelectedDistrict(null)
    setSelectedQuarter(null)
    setSelectedBuilding(null)

    switch (activeTab) {
      case TAB_ID.NETWORK:
        setSelectedCity(null)
        setMapState({ ncType: '4G', kmlContent: '' })
        break
      case TAB_ID.HBB:
        setMapState({ kmlHbb: `${NETWORK_COVERAGE_URL}/Urhiin_Internet.kml` })
        break
      case TAB_ID.WIFI:
        setMapState({ kmlWifi: `${NETWORK_COVERAGE_URL}/MobinetWifi.kml` })
        break
      case TAB_ID.TRAVEL:
        void fetchTravelPoint({ cityzipcode: toOptional(selectedCity?.zipcode) })
        setMapState({ kmlContent: '' })
        break
      default:
        setMapState({ kmlContent: '' })
    }
  }, [activeTab]) // fetchTravelPoint and selectedCity?.zipcode intentionally excluded — tab switch resets to unfiltered list

  // ─── Fetchers ─────────────────────────────────────────────────────────────

  const fetchCities = async (): Promise<void> => {
    try {
      const response = await getCityList()
      if (response != null) setCities(sortCities(response as unknown as City[]))
    } catch (error) {
      console.error('Error fetching cities:', error)
    }
  }

  const fetchDistricts = async (query: DistrictFilter): Promise<void> => {
    try {
      const response = await getDistrictList(query)
      if (response != null) setDistricts(sortByNameMn(response as unknown as District[]))
    } catch (error) {
      console.error('Error fetching districts:', error)
    }
  }

  const fetchQuarters = async (query: QuarterFilter): Promise<void> => {
    try {
      const response = await getQuarterList(query)
      if (response != null) setQuarters(sortByNameMn(response as unknown as Quarter[]))
    } catch (error) {
      console.error('Error fetching quarters:', error)
    }
  }

  const fetchBuildings = async (quarterZip: string): Promise<void> => {
    try {
      const response = await NetworkCoverageAPI.getBuildingList({ quarterZip })
      if (response?.result != null) {
        setBuildings((response.result as unknown as Building[]).filter((b) => b.locationStatusId === 1))
      }
    } catch (error) {
      console.error('Error fetching buildings:', error)
    }
  }

  const fetchTravelPoint = async (query: TravelPointFilter): Promise<void> => {
    try {
      const response = await getTravelPoint(query)
      if (response != null) {
        const points = response as unknown as TravelPoint[]
        setTravelPoints(sortByNameMn(points))
        setMapState({
          markers: points.map((item) => ({
            id: item.id,
            travelPoint: item,
            lat: parseFloat(item.latitude),
            lng: parseFloat(item.longitude)
          }))
        })
      }
    } catch (error) {
      console.error('Error fetching travel points:', error)
    }
  }

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleCitySelect = (
    zipcode: string,
    options: { updateMapCenter?: boolean, onCityFound?: (city: City) => void } = {}
  ): void => {
    const { updateMapCenter = true, onCityFound } = options
    const city = cities.find((c) => c.zipcode === zipcode) ?? null
    setSelectedCity(city)
    setSelectedDistrict(null)
    setDistricts([])
    setSelectedQuarter(null)
    setQuarters([])
    setBuildings([])
    setSelectedBuilding(null)
    if (city !== null) {
      void fetchDistricts({ cityzipcode: toOptional(city.zipcode) })
      if (updateMapCenter) {
        setMapState({
          center: { lat: parseFloat(city.latitude), lng: parseFloat(city.longitude) },
          zoom: 12
        })
      }
      onCityFound?.(city)
    }
  }

  const handleDistrictSelect = (
    zipcode: string,
    options: { updateMapCenter?: boolean } = {}
  ): void => {
    const { updateMapCenter = true } = options
    const district = districts.find((d) => d.zipcode === zipcode) ?? null
    setSelectedDistrict(district)
    setSelectedQuarter(null)
    setQuarters([])
    setBuildings([])
    setSelectedBuilding(null)
    if (district !== null) {
      void fetchQuarters({ districtzipcode: toOptional(district.zipcode) })
      if (updateMapCenter) {
        setMapState({
          center: { lat: parseFloat(district.latitude), lng: parseFloat(district.longitude) },
          zoom: 15
        })
      }
    }
  }

  const handleQuarterSelect = (event: SelectChangeEvent<string>): void => {
    const quarter = quarters.find((q) => q.zipcode === event.target.value) ?? null
    setSelectedQuarter(quarter)
    setBuildings([])
    setSelectedBuilding(null)
    if (quarter !== null) void fetchBuildings(quarter.zipcode)
  }

  const handleBuildingSelect = (event: SelectChangeEvent<string>): void => {
    setSelectedBuilding(buildings.find((b) => b.id === event.target.value) ?? null)
  }

  const handleNetworkTypeSelect = (event: SelectChangeEvent<string>): void => {
    setSelectedCity(null)
    setSelectedDistrict(null)
    setMapState({ ncType: event.target.value, kmlContent: '', overlays: undefined })
  }

  const handleTravelPointSelect = (item: TravelPoint): void => {
    setMapState({
      center: { lat: parseFloat(item.latitude), lng: parseFloat(item.longitude) },
      selectedTravelPoint: item
    })
    setSelectedTravelPoint(item)
  }

  // ─── Derived select items ──────────────────────────────────────────────────

  const cityItems: SelectItem[] = cities.map((c) => ({
    key: c.zipcode,
    value: c.zipcode,
    label: localeName(c, locale)
  }))

  const districtItems: SelectItem[] = districts.map((d) => ({
    key: d.zipcode,
    value: d.zipcode,
    label: localeName(d, locale)
  }))

  const quarterItems: SelectItem[] = quarters.map((q) => ({
    key: q.zipcode,
    value: q.zipcode,
    label: localeName(q, locale)
  }))

  const buildingItems: SelectItem[] = buildings.map((b) => ({
    key: b.id,
    value: b.id,
    label: `${b.horoolol !== '' ? b.horoolol + ' ' : ''}${b.building}`
  }))

  const ncCityItems: SelectItem[] = cities.map((c) => ({
    key: c.zipcode,
    value: c.zipcode,
    label: localeName(c, locale)
  }))

  const ncDistrictItems: SelectItem[] = districts
    .filter((d) => {
      if (mapState.ncType === '4G') return Boolean(d.fourG)
      if (mapState.ncType === '5G') return Boolean(d.fiveG)
      return true
    })
    .map((d) => ({
      key: d.zipcode,
      value: d.zipcode,
      label: localeName(d, locale)
    }))

  return {
    mapState,
    setMapState,
    activeTab,
    setActiveTab,
    cities,
    selectedCity,
    setSelectedCity,
    districts,
    selectedDistrict,
    quarters,
    selectedQuarter,
    buildings,
    selectedBuilding,
    travelPoints,
    selectedTravelPoint,
    isHbbModalOpen,
    setIsHbbModalOpen,
    isNetworkModalOpen,
    setIsNetworkModalOpen,
    userLocation,
    setUserLocation,
    handleCitySelect,
    handleDistrictSelect,
    handleQuarterSelect,
    handleBuildingSelect,
    handleNetworkTypeSelect,
    handleTravelPointSelect,
    fetchTravelPoint,
    cityItems,
    districtItems,
    quarterItems,
    buildingItems,
    ncCityItems,
    ncDistrictItems
  }
}
